import React, { useMemo, useState } from 'react';
import {
  MdOutlineCheckBox,
  MdOutlineCheckBoxOutlineBlank
} from 'react-icons/md';

import { Flex, Icon, Label, LssProp } from '@lucidhq/lucidium';

import { TemplateQuestion } from '~/components/Shared/hooks/useFetchTemplateQuestions';
import { InfoTooltip } from '~/impact-measurement/components';
import { t } from '~/utils/i18n';
import { EnhancedTemplateQuestion } from '~/utils/templateQuestions';

import { useCampaignManager } from '../../CampaignManagerProvider';
import { SurveyStepIds } from '../../useCampaignManagerSteps';
import { useBehavioralQuestions } from '../BehavioralQuestionsStep/BehavioralQuestionsProvider';
import { useStandardQuestionsProvider } from '../StandardQuestionsStep/StandardQuestionsProvider';
import { QUESTION_LOGIC_ITEMS } from './QuestionLogicDrawer';

const itemWrapperLss: LssProp = {
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: 'regular',
  borderTop: 'regular',
  borderColor: 'gray500',
  height: '3rem',
  mx: 'xs',
  py: 'sm'
};

const labelLss: LssProp = {
  mb: 'none',
  fontSize: '1rem',
  color: 'gray700',
  mr: 'none',
  pr: 'xs'
};

const readOnlyIconLss: LssProp = {
  cursor: 'not-allowed',
  color: 'gray700'
};

type AnchoredAnswer = string | null;

/*** "Other" Answer option Getters ***** */
const getOtherAnswerOption = (val: AnchoredAnswer) => {
  if (val === null) {
    return t('forms.labels.other');
  }
  return null;
};

const collectUpdatedAnswers = (
  val: string,
  answers: string[],
  question: EnhancedTemplateQuestion
) => {
  if (
    !!question?.none_of_the_above_answer &&
    answers.includes(question.none_of_the_above_answer)
  ) {
    const copy = [...answers];

    copy.splice(answers.length - 1, 0, val);

    return copy;
  }

  return [...answers, val];
};

const getUpdatedAnswerRecord = (
  val: AnchoredAnswer,
  question: EnhancedTemplateQuestion
) => {
  if (!!question.use_answer_meta) {
    const updatedAnswers =
      val === null
        ? question?.answers.filter(answer => answer !== question?.other_answer)
        : collectUpdatedAnswers(val, question.answers, question);

    return {
      answers: updatedAnswers,
      ...(question.meta.isCustomKPI && {
        defaultBrandAnswers: updatedAnswers
      })
    };
  }

  const answers = question?.customAnswers ?? question.answers;
  const updatedCustomAnswers =
    val === null
      ? question?.customAnswers?.filter(
          answer => answer !== question?.other_answer
        )
      : collectUpdatedAnswers(val, answers, question);

  return {
    answers: updatedCustomAnswers,
    ...(question.meta.isCustomKPI && {
      customAnswers: updatedCustomAnswers
    })
  };
};

/***** NONE OF THE ABOVE Getters ***** */
type NotaVal = string | null;

const getNoneOfTheAboveAnswer = (val: NotaVal, translatedNOTAValue: string) => {
  if (val === null && !!translatedNOTAValue?.length) {
    return translatedNOTAValue;
  }

  return null;
};

/**
 * This method is needed in order to remove extra copies of the NOTA value
 * Due to the default set of answers containing the NOTA value there are scenarios in which duplicate NOTA values can exist
 * @param answers string array
 * @returns an array of answers either including or excluding a NOTA value based off if it exists already or not.
 */
const getUniqueAnswers = (
  answers: string[],
  translatedNOTAVal: string,
  currentNOTA?: string | null
) => {
  return answers.includes(translatedNOTAVal) ||
    (!!currentNOTA && answers.includes(currentNOTA))
    ? answers.slice(0, answers.length - 1)
    : [...answers, translatedNOTAVal];
};

/**
 * This method takes in the NOTA value and the question and returns an object of either { answers, defaultBrandAnswers } or {answers, customAnswers}.
 * The reason that there are duplicate conditions is due to having default answer set and for the fully custom answer set.
 * @param val NotaVal
 * @param question EnhancedTemplateQuestion
 * @returns - { answers, defaultBrandAnswers } || { answers, customAnswers }
 */
const getUpdatedCustomAnswers = (
  val: NotaVal,
  translatedNOTAVal: string,
  question?: EnhancedTemplateQuestion
): Partial<EnhancedTemplateQuestion> => {
  // If question is using default brand answers reset copies of answers and defaultBrandAnswers
  if (!!question?.use_answer_meta) {
    const updatedAnswers =
      val === null
        ? question?.answers?.filter(
            answer =>
              answer !== translatedNOTAVal &&
              answer !== question.none_of_the_above_answer
          )
        : getUniqueAnswers(
            question.answers,
            translatedNOTAVal,
            question?.none_of_the_above_answer
          );

    return { answers: updatedAnswers, defaultBrandAnswers: updatedAnswers };
  }

  const updatedCustomAnswers =
    val === null
      ? question?.customAnswers?.filter(
          answer =>
            answer !== translatedNOTAVal &&
            answer !== question.none_of_the_above_answer
        )
      : getUniqueAnswers(
          question?.answers as string[],
          translatedNOTAVal,
          question?.none_of_the_above_answer
        );

  return {
    customAnswers: updatedCustomAnswers,
    answers: updatedCustomAnswers
  };
};

type QuestionLogicItemProps = {
  itemName: string;
  itemValue: any;
  isItemChecked: boolean;
  isReadOnly: boolean;
  onSave: (data: Partial<TemplateQuestion>) => void;
  wrapperLss?: LssProp;
  question?: EnhancedTemplateQuestion;
  hasTooltip?: boolean;
  tooltipTitle?: string;
};

export const QuestionLogicItem = ({
  itemName,
  itemValue,
  isItemChecked,
  isReadOnly,
  wrapperLss,
  onSave,
  question,
  hasTooltip = false,
  tooltipTitle = ''
}: QuestionLogicItemProps) => {
  const [isChecked, setChecked] = useState<boolean | null>(null);
  const defaultCheckedValue = useMemo(() => {
    return typeof isChecked === 'boolean' ? isChecked : isItemChecked;
  }, [isItemChecked, isChecked]);

  const { options } = useCampaignManager();

  const { translatedNOTAValue = '' } = useStandardQuestionsProvider();
  const { referenceNOTAValue = '' } = useBehavioralQuestions();

  const isStandard =
    options?.activeChildStep === SurveyStepIds.STANDARD_QUESTIONS;

  const currentNOTAValue = isStandard
    ? translatedNOTAValue
    : referenceNOTAValue;

  return (
    <Flex lss={{ ...itemWrapperLss, ...wrapperLss }}>
      <Flex lss={{ flexDirection: 'row' }}>
        <Label lss={labelLss}>{t(`forms.labels.${itemName}`)}</Label>
        {hasTooltip && (
          <InfoTooltip
            enforceRefWrapper
            title={tooltipTitle}
            lss={{ ml: 'md', p: 'md' }}
          />
        )}
      </Flex>

      <Flex
        aria-checked={defaultCheckedValue}
        as="a"
        href="#"
        role="checkbox"
        onClick={(e: React.SyntheticEvent) => {
          e.preventDefault();
          if (isReadOnly) return;

          setChecked(() => !defaultCheckedValue);
          if (itemName === QUESTION_LOGIC_ITEMS.OTHER) {
            const otherAnswerValue = getOtherAnswerOption(itemValue);

            const anchoredAnswerRecord = getUpdatedAnswerRecord(
              otherAnswerValue,
              question as EnhancedTemplateQuestion
            );

            onSave({
              [itemName]: otherAnswerValue,
              ...anchoredAnswerRecord
            });
          } else if (itemName === QUESTION_LOGIC_ITEMS.NOTA) {
            const notaValue = getNoneOfTheAboveAnswer(
              itemValue,
              currentNOTAValue
            );

            const updatedAnswerRecord = getUpdatedCustomAnswers(
              notaValue,
              currentNOTAValue,
              question
            );

            onSave({
              [itemName]: notaValue,
              ...updatedAnswerRecord
            });
          } else {
            onSave({
              [itemName]: !defaultCheckedValue,

              ...(itemName === QUESTION_LOGIC_ITEMS.CUSTOM_QUESTION_TEXT && {
                custom_question_text: ''
              })
            });
          }
        }}
      >
        <Icon
          as={
            defaultCheckedValue
              ? MdOutlineCheckBox
              : MdOutlineCheckBoxOutlineBlank
          }
          size={28}
          lss={{
            cursor: 'pointer',
            color: 'secondary500',
            ...(isReadOnly && readOnlyIconLss)
          }}
        />
      </Flex>
    </Flex>
  );
};
